import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams, useHistory } from "react-router-dom";
import { LocalShippingTwoTone } from "@mui/icons-material";
import { Box, CircularProgress, Typography, Paper, NativeSelect, FormControl } from "@mui/material";
import { apiGet, apiPut } from "../api";
import { UserDataContext } from "../app/App";
import Layout from "../components/Layout/Layout";
import WorkshopOrdersList from "../components/WorkshopOrdersList/WorkshopOrdersList";

function Equipment() {
    const { user } = useContext(UserDataContext);
    const queryClient = useQueryClient();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { equipmentId } = useParams();

    const [group, setGroup] = useState({ id: '', name: '' });
    const [condition, setCondition] = useState({ code: '', label: '' });

    // Get equipment
    const {
        data: equipment,
        isLoading: isLoadingEquipment,
        isError: isErrorEquipment,
    } = useQuery(["equipment", equipmentId], () => apiGet(`/api/common/equipment/${equipmentId}`));
    // Get workshop orders
    const { data: workshopOrders, isLoading: isLoadingWorkshopOrders } = useQuery(["workshopOrders", equipmentId], () =>
        apiGet(`/api/workshop/GetWorkshopOrdersByEquipmentId/${equipmentId}`)
    );
    // Get equipment groups
    const { data: groups = [], isLoading: isLoadingGroups } = useQuery("groups", () => apiGet(`/api/common/equipment-groups`), { staleTime: Infinity });
    // Get equipment conditions
    const { data: conditionsData = {}, isLoading: isLoadingConditions } = useQuery("conditions", () => apiGet("/api/common/equipment-conditions"), { staleTime: Infinity });

    const conditions = useMemo(() => {
        return Object.entries(conditionsData).map(([code, label]) => ({ code, label }));
    }, [conditionsData])

    const updateEquipmentGroup = useMutation(
        ({ equipmentId, groupId }) => apiPut(`/api/common/equipment-groups`, { equipmentId, groupId }),
        {
            onSuccess: () => {
                enqueueSnackbar("Group updated successfully", { variant: "success" });
                queryClient.invalidateQueries(["equipment", equipmentId]);
            },
            onError: () => {
                enqueueSnackbar(`Error updating group`, { variant: "error" });
            },
        }
    );
    const updateEquipmentCondition = useMutation(
        ({ equipmentId, condition }) => apiPut("/api/common/equipment-conditions", { equipmentId, condition }),
        {
            onSuccess: () => {
                enqueueSnackbar("Condition updated successfully", { variant: "success" });
                queryClient.invalidateQueries(["equipment", equipmentId]);
            },
            onError: () => {
                enqueueSnackbar("Error updating condition", { variant: "error" });
            }
        }
    );

    useEffect(() => {
        if (equipment) {
            setGroup(equipment.group ? { id: equipment.group.id, name: equipment.group.name } : { id: '', name: '' });

            if (equipment.condition && conditionsData) {
                const conditionEntry = Object.entries(conditionsData).find(([_, label]) => label === equipment.condition);
                if (conditionEntry) {
                    const [code, label] = conditionEntry;
                    setCondition({ code, label });
                } else {
                    setCondition({ code: '', label: '' })
                }
            }
        }
    }, [equipment, conditionsData, conditions]);

    const handleGroupChange = (event) => {
        const newGroupId = event.target.value;
        if (group?.id === newGroupId) {
            return;
        }
        const newGroupName = groups.find((g) => g?.id === newGroupId)?.name;
        if (!newGroupName) {
            return;
        }
        setGroup({ id: newGroupId, name: newGroupName });

        updateEquipmentGroup.mutate(
            { equipmentId: equipmentId, groupId: newGroupId },
            { onError: () => { setGroup(equipment?.group ?? { id: '', name: '' }); }}
        );
    };

    const handleConditionChange = (event) => {
        const newConditionCode = event.target.value;
        if (condition.code === newConditionCode) {
            return;
        }
        const newConditionLabel = conditions.find((c) => c?.code === newConditionCode)?.label;
        if (!newConditionLabel) {
            return;
        }
        setCondition({ code: newConditionCode, label: newConditionLabel });

        updateEquipmentCondition.mutate(
            { equipmentId: equipmentId, condition: Number(newConditionCode) },
            { onError: () => { setGroup({ code: '', label: '' }); } } // TODO
        );
    }

    if (isErrorEquipment) {
        enqueueSnackbar("There are no workshop orders for that vehicle", {
            variant: "warning",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 6000,
            preventDuplicate: true,
        });
        history.replace("/workshop");
    }

    if (isLoadingEquipment || isLoadingWorkshopOrders || isLoadingGroups || isLoadingConditions) {
        return (
            <Layout color="#ff9602" title={`Equipment`} subtitle={user.name}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const numWorkshopOrders = workshopOrders ? workshopOrders.length : 0;
    const { number = "N/A", vehicleType = {} } = equipment || {};
    const type = vehicleType?.type || "N/A";
    console.log(group)
    return (
        <Layout color="#ff9602" title={`Equipment`} subtitle={number}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Equipment card */}
                <Box
                    component={Paper}
                    variant="outlined"
                    sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
                    elevation={0}
                >
                    <LocalShippingTwoTone />
                    <Typography style={{ fontWeight: "bold" }}>{`${type} - ${number}`}</Typography>
                </Box>

                {/* Equipment info */}
                <Box
                    component={Paper}
                    variant="outlined"
                    sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
                    elevation={0}
                >
                    {/* Condition */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography color="textSecondary">{`Condition`}</Typography>
                        <FormControl sx={{ width: "60%" }}>
                            <NativeSelect
                                disabled={
                                    updateEquipmentCondition.isLoading ||
                                    isLoadingConditions ||
                                    !conditions.length
                                }
                                value={condition.code}
                                onChange={handleConditionChange}
                            >
                                {!isLoadingConditions && conditions?.map((c) => (
                                    <option value={c.code} key={c.code}>{c.label}</option>
                                ))}
                            </NativeSelect>
                        </FormControl>
                    </Box>

                    {/* Group */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography color="textSecondary">{`Group`}</Typography>
                        <FormControl sx={{ width: "60%" }}>
                            <NativeSelect
                                disabled={
                                    updateEquipmentGroup.isLoading ||
                                    isLoadingGroups ||
                                    !groups.length
                                }
                                value={group.id}
                                onChange={handleGroupChange}
                            >
                                {group.id === '' && <option value="">Select a group</option>}
                                {!isLoadingGroups && groups?.map((g) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </NativeSelect>
                        </FormControl>
                    </Box>

                    {/* Type */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography color="textSecondary">{`Type`}</Typography>
                        <Typography>{type ? type : "-"}</Typography>
                    </Box>
                </Box>

                {/* Workshop orders */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box display="flex" alignItems="flex-end">
                        <Box mr={1}>
                            <Typography variant="h6" component="span" color="primary" style={{ fontWeight: "bold" }}>
                                {isLoadingWorkshopOrders ? "" : numWorkshopOrders}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="h6" style={{ fontWeight: "normal" }}>
                                {isLoadingWorkshopOrders
                                    ? ""
                                    : numWorkshopOrders === 1
                                    ? "Workshop Order"
                                    : "Workshop Orders"}
                            </Typography>
                        </Box>
                    </Box>
                    {!isLoadingWorkshopOrders && workshopOrders && <WorkshopOrdersList orders={workshopOrders} />}
                </Box>
            </Box>
        </Layout>
    );
}

export default Equipment;
